import {
  SETUP_CATEGORY_DEFINITIONS,
  getSetupPageData,
  getTagSummaries,
  parseCreatorName,
} from './setupContent';

export const SEARCH_HUB_DEFINITIONS = [
  {
    slug: 'desk-setup-ideas',
    shortLabel: 'Desk setup ideas',
    title: 'Desk setup ideas for real workspaces',
    description:
      'Browse real desk setup ideas, discover tagged gear, and move from broad inspiration into home office, minimalist, and component-led setup pages on 101Setups.',
    intro:
      'This is the broadest crawlable entry point in the new 101Setups search architecture. It exists to answer the main desk setup ideas intent honestly, then route readers deeper into narrower hubs like minimalist desk setups, home office desk setups, and gear-led topic pages that already have stronger visual proof.',
    whyItExists:
      '101Setups needs a real search entry point that can introduce the site, explain how to browse it, and distribute link equity into narrower hubs instead of relying on a raw feed alone.',
    supportTitle: 'Best next paths from here',
    supportCopy:
      'Readers who land here should quickly branch into tighter intents like minimalist, home office, cable management, monitor arms, desk lighting, and small-space setups.',
    keywords: ['desk', 'setup', 'workspace', 'home office', 'monitor', 'keyboard', 'lamp', 'cable', 'lighting'],
    tagCategories: ['Desk', 'Monitor', 'Monitorstand', 'Cablemanagement', 'Light', 'Lamp', 'Keyboard', 'Mouse', 'Notebook'],
    preferredCategorySlugs: ['cable-management', 'monitor-arms', 'desk-lighting', 'small-desk', 'desk-shelves'],
    siblingSlugs: ['how-to-hide-cables-on-a-desk', 'desk-lighting-ideas', 'monitor-arm-desk-setup', 'home-office-desk-setup'],
    relatedTopicSlugs: ['cable-management', 'monitor-arms', 'desk-lighting', 'small-desk'],
  },
  {
    slug: 'minimalist-desk-setup',
    shortLabel: 'Minimalist setups',
    title: 'Minimalist desk setup ideas for cleaner workspaces',
    description:
      'Browse minimalist desk setup ideas built from real workspaces, with cleaner cable routing, lighter visual clutter, and tagged gear discovery on 101Setups.',
    intro:
      'Minimalist desk setups are not just white desks and empty surfaces. The good ones reduce clutter, hide cable noise, keep only useful gear in view, and make the workspace feel calm enough to actually use. This hub pulls together the cleanest setups the current 101Setups library can support while the editorial layer keeps getting stronger.',
    whyItExists:
      'Minimalist desk setup is a strong non-brand search theme because the intent is clear, the visuals matter, and the page can route readers into cleaner accessory and cable-management clusters without pretending to be a generic review site.',
    supportTitle: 'What minimalist usually overlaps with',
    supportCopy:
      'The strongest minimalist setups often overlap with cable management, monitor arms, desk shelves, and restrained lighting choices rather than loud decor or crowded accessory lists.',
    keywords: ['minimal', 'minimalist', 'clean', 'simple', 'white', 'wood', 'walnut', 'neutral', 'wireless', 'light bar'],
    tagCategories: ['Cablemanagement', 'Monitorstand', 'Light', 'Lamp', 'Desk'],
    preferredCategorySlugs: ['cable-management', 'desk-shelves', 'monitor-arms', 'desk-lighting'],
    siblingSlugs: ['desk-setup-ideas', 'how-to-hide-cables-on-a-desk', 'desk-lighting-ideas', 'monitor-arm-desk-setup'],
    relatedTopicSlugs: ['cable-management', 'desk-shelves', 'monitor-arms', 'desk-lighting'],
  },
  {
    slug: 'home-office-desk-setup',
    shortLabel: 'Home office',
    title: 'Home office desk setup ideas for real workdays',
    description:
      'Explore home office desk setup ideas from real workspaces, including monitor layouts, desk lighting, compact productivity gear, and tagged product discovery on 101Setups.',
    intro:
      'Home office desk setup searches usually come from people trying to build a workspace they can actually work from every day, not just admire for ten seconds on social media. This page leans into practical layouts, work-friendly screens and lighting, and gear combinations that feel believable for real desk time.',
    whyItExists:
      'Home office is one of the clearest high-intent routes into 101Setups because it connects inspiration with practical buying and layout questions without needing inflated authority claims.',
    supportTitle: 'What home office readers usually want next',
    supportCopy:
      'The natural next clicks from this intent are monitor arms, desk lighting, cable management, small-space setups, and broader desk setup idea pages.',
    keywords: ['home office', 'office', 'monitor', 'notebook', 'laptop', 'lamp', 'desk', 'keyboard', 'mouse', 'speaker', 'webcam'],
    tagCategories: ['Monitor', 'Monitorstand', 'Lamp', 'Light', 'Notebook', 'Desk', 'Keyboard', 'Mouse'],
    preferredCategorySlugs: ['monitor-arms', 'desk-lighting', 'small-desk', 'cable-management'],
    siblingSlugs: ['desk-setup-ideas', 'how-to-hide-cables-on-a-desk', 'desk-lighting-ideas', 'monitor-arm-desk-setup'],
    relatedTopicSlugs: ['monitor-arms', 'desk-lighting', 'small-desk', 'cable-management'],
  },
  {
    slug: 'desk-lighting-ideas',
    shortLabel: 'Desk lighting',
    title: 'Desk lighting ideas for real workspaces',
    description:
      'Explore desk lighting ideas for real workspaces, compare when monitor light bars, desk lamps, and narrower lighting branches make the most sense, and route into home office, small desk, dual monitor, and video-call setup pages on 101Setups.',
    intro:
      'Desk lighting is broader than buying one lamp. The honest next move depends on glare control, task visibility, screen depth, whether the desk is compact or shared with a laptop camera, and whether a lamp, a monitor light bar, or a narrower branch should decide the setup next. This page exists to own that broader reader intent at one obvious top-level surface before routing into the stronger supporting pages that already exist.',
    whyItExists:
      '101Setups already has a deep lighting support graph across the desk-lighting topic hub, authored collections, and lighting gear pages. This canonical surface gives broad lighting intent a clear search-ready entry without forcing readers to treat the category feed as the primary editorial router.',
    supportTitle: 'Best next lighting paths from here',
    supportCopy:
      'Most readers should branch into the main lighting system routes first, then narrow into home office, small desk, dual monitor, video-call, lamp-first, or light-bar-first pages once the real desk constraint is obvious.',
    keywords: [
      'desk lighting',
      'lighting',
      'lamp',
      'desk lamp',
      'light bar',
      'monitor light',
      'monitor light bar',
      'home office',
      'small desk',
      'dual monitor',
      'video call',
      'glare',
    ],
    tagCategories: ['Light', 'Lamp', 'Monitor', 'Desk'],
    preferredCategorySlugs: ['desk-lighting', 'small-desk', 'standing-desk', 'monitor-arms'],
    siblingSlugs: ['desk-setup-ideas', 'home-office-desk-setup', 'minimalist-desk-setup'],
    relatedTopicSlugs: ['desk-lighting', 'small-desk', 'standing-desk', 'monitor-arms'],
  },
  {
    slug: 'monitor-arm-desk-setup',
    shortLabel: 'Monitor arms',
    title: 'Monitor arm desk setup ideas for cleaner screen layouts',
    description:
      'Explore monitor arm desk setup ideas, see when a monitor arm actually improves screen position and desk depth, and route into home office, small desk, standing desk, dual monitor, ultrawide, and non-arm alternatives on 101Setups.',
    intro:
      'Monitor arm desk setup searches are rarely just about buying one arm. The honest decision is whether desk depth, clamp fit, screen count, movement range, note-taking space, and camera lines make an arm the cleanest support choice or whether a riser, stand, or shelf keeps the desk more believable. This page exists to own that broader support decision before routing into the stronger monitor-arm and non-arm branches that already exist.',
    whyItExists:
      '101Setups already has a deep monitor-support graph across the monitor-arms topic hub, authored monitor-arm leaves, and adjacent riser, stand, and shelf pages. This canonical surface gives the broad intent one obvious top-level search entry without pretending every desk should default to an arm-first answer.',
    supportTitle: 'Best next monitor-support paths from here',
    supportCopy:
      'Most readers should branch quickly into the desk shape that matters most, then compare the strongest arm-specific routes against honest non-arm alternatives when clamp fit, shelf depth, or movement needs change the answer.',
    keywords: [
      'monitor arm',
      'monitor arms',
      'monitor mount',
      'screen arm',
      'dual monitor',
      'ultrawide',
      'standing desk',
      'small desk',
      'desk shelf',
      'monitor stand',
      'monitor riser',
    ],
    tagCategories: ['Monitorstand', 'Monitor', 'Desk'],
    preferredCategorySlugs: ['monitor-arms', 'desk-shelves', 'small-desk', 'standing-desk'],
    siblingSlugs: ['desk-setup-ideas', 'home-office-desk-setup', 'minimalist-desk-setup'],
    relatedTopicSlugs: ['monitor-arms', 'desk-shelves', 'small-desk', 'standing-desk'],
  },
  {
    slug: 'how-to-hide-cables-on-a-desk',
    shortLabel: 'Hide cables',
    title: 'How to hide cables on a desk without making the setup worse',
    description:
      'Explore how to hide cables on a desk, compare trays, power-strip mounts, sleeves, raceways, and narrower desk-specific cable paths, and route into home office, standing desk, monitor-arm, and compact workspace cable ideas on 101Setups.',
    intro:
      'How to hide cables on a desk is broader than buying one tray or pack of clips. The honest next move depends on whether the setup needs one calmer power zone, cleaner under-desk routing, tighter edge guidance, a standing-desk movement path, or a narrower desk-type branch. This page exists to own that broader reader intent at one obvious top-level surface before routing into the stronger cable and power-zone pages that already exist.',
    whyItExists:
      '101Setups already has a deep cable-management support graph across the cable-management topic hub, authored collections, and cable or power-zone gear leaves. This canonical surface gives broad cable-hiding intent one clear search-ready entry without pretending the category feed is already the best reader-facing router.',
    supportTitle: 'Best next cable-hiding paths from here',
    supportCopy:
      'Most readers should branch quickly into the desk shape or cable system that actually defines the mess: broad desk routing, home office, small desk, standing desk, laptop-and-monitor, monitor-arm cable paths, or a power-strip reset when the real bottleneck is the power zone.',
    keywords: [
      'hide cables',
      'desk cable management',
      'wire management',
      'power strip',
      'raceway',
      'cable tray',
      'cable sleeves',
      'cable clips',
      'standing desk',
      'monitor arm',
    ],
    tagCategories: ['Cablemanagement', 'Desk', 'Monitorstand', 'Notebook'],
    preferredCategorySlugs: ['cable-management', 'monitor-arms', 'standing-desk', 'small-desk'],
    siblingSlugs: ['desk-setup-ideas', 'home-office-desk-setup', 'minimalist-desk-setup'],
    relatedTopicSlugs: ['cable-management', 'monitor-arms', 'standing-desk', 'small-desk'],
  },
];

const normalize = value => String(value || '').toLowerCase();

const scoreKeywordMatch = (value, keyword) => {
  if (!value || !keyword) {
    return 0;
  }

  return value.includes(keyword) ? (keyword.includes(' ') ? 5 : 2) : 0;
};

export const getSearchHubBySlug = slug => SEARCH_HUB_DEFINITIONS.find(hub => hub.slug === slug) || null;

export const getSearchHubRelatedCategories = hub => {
  if (!hub) {
    return SETUP_CATEGORY_DEFINITIONS.slice(0, 4);
  }

  return hub.relatedTopicSlugs
    .map(slug => SETUP_CATEGORY_DEFINITIONS.find(category => category.slug === slug))
    .filter(Boolean)
    .slice(0, 4);
};

export const getSearchHubSiblingPages = hub => {
  if (!hub) {
    return [];
  }

  return hub.siblingSlugs
    .map(getSearchHubBySlug)
    .filter(Boolean)
    .slice(0, 3);
};

export const scorePostForSearchHub = (post, hub) => {
  if (!post || !hub) {
    return 0;
  }

  const tags = getTagSummaries(post);
  const setup = getSetupPageData(post);
  const creator = normalize(parseCreatorName(post.userid));
  let score = 0;

  if (hub.preferredCategorySlugs.includes(setup.primaryCategory?.slug || '')) {
    score += 8;
  }

  if (hub.slug === 'desk-setup-ideas') {
    score += Math.min(setup.tags.length, 5);
  }

  tags.forEach(tag => {
    const lowerName = normalize(tag.name);
    const lowerCategory = normalize(tag.category);

    if (hub.tagCategories.map(category => normalize(category)).includes(lowerCategory)) {
      score += 3;
    }

    hub.keywords.forEach(keyword => {
      score += scoreKeywordMatch(lowerName, normalize(keyword));
    });
  });

  hub.keywords.forEach(keyword => {
    score += scoreKeywordMatch(creator, normalize(keyword));
    score += scoreKeywordMatch(normalize(setup.title), normalize(keyword));
    score += scoreKeywordMatch(normalize(setup.description), normalize(keyword));
  });

  return score;
};

export const getRankedPostsForSearchHub = (posts, hub) =>
  [...posts]
    .map(post => ({
      post,
      score: scorePostForSearchHub(post, hub),
    }))
    .sort((left, right) => {
      if (right.score !== left.score) {
        return right.score - left.score;
      }

      const leftDate = new Date(left.post?.createdAt || left.post?.timestamp || 0).getTime();
      const rightDate = new Date(right.post?.createdAt || right.post?.timestamp || 0).getTime();

      return rightDate - leftDate;
    });
